import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, LogOut, Search, RefreshCw, Download, Trash2, ChevronRight, BarChart3, TrendingUp, Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SECTORS } from "@/lib/sectors";
import { api, getToken, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { ADMIN, LOGOUT } from "@/constants/testIds";

const STATUS_OPTIONS = [
    { key: "new", label: "New", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { key: "in_review", label: "In review", tone: "bg-blue-50 text-blue-700 border-blue-200" },
    { key: "contacted", label: "Contacted", tone: "bg-amber-50 text-amber-700 border-amber-200" },
    { key: "won", label: "Won", tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    { key: "lost", label: "Lost", tone: "bg-rose-50 text-rose-700 border-rose-200" },
];

function statusBadge(s) {
    const found = STATUS_OPTIONS.find((x) => x.key === s) || STATUS_OPTIONS[0];
    return <Badge className={`${found.tone} border font-semibold capitalize`}>{found.label}</Badge>;
}

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ sector: "all", status: "all", q: "" });
    const [selected, setSelected] = useState(null);
    const [draftStatus, setDraftStatus] = useState("");
    const [draftNotes, setDraftNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter.sector && filter.sector !== "all") params.sector = filter.sector;
            if (filter.status && filter.status !== "all") params.status = filter.status;
            if (filter.q) params.q = filter.q;
            const [enq, s] = await Promise.all([
                api.get("/admin/enquiries", { params }),
                api.get("/admin/stats"),
            ]);
            setItems(enq.data.items);
            setTotal(enq.data.total);
            setStats(s.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    function openEnquiry(e) {
        setSelected(e);
        setDraftStatus(e.status);
        setDraftNotes(e.notes || "");
    }

    async function saveEnquiry() {
        if (!selected) return;
        setSaving(true);
        try {
            const { data } = await api.patch(`/admin/enquiries/${selected.id}`, {
                status: draftStatus,
                notes: draftNotes,
            });
            toast.success("Enquiry updated.");
            setSelected(data);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    async function deleteEnquiry() {
        if (!selected) return;
        if (!window.confirm(`Delete enquiry ${selected.reference}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/enquiries/${selected.id}`);
            toast.success("Enquiry deleted.");
            setSelected(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function exportCsv() {
        try {
            const res = await fetch(`${API_BASE}/admin/enquiries/export`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) throw new Error("Export failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `claimhub-enquiries-${Date.now()}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Couldn&apos;t export. Try again.");
        }
    }

    function handleLogout() {
        logout();
        navigate("/admin/login");
    }

    return (
        <div data-testid={ADMIN.dashboardRoot} className="min-h-screen bg-slate-50">
            {/* Top bar */}
            <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                <div className="container-x flex items-center justify-between h-16">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl bg-navy-900 flex items-center justify-center">
                                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                            </div>
                            <div className="font-display font-bold text-navy-900">ClaimHub <span className="text-emerald-600">UK</span></div>
                        </Link>
                        <span className="hidden sm:inline-flex ml-2 px-2 py-1 rounded-md bg-navy-900 text-emerald-300 text-[10px] font-bold uppercase tracking-wider">Admin</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="hidden sm:inline text-sm text-slate-600">{user?.email}</span>
                        <button
                            onClick={handleLogout}
                            data-testid={ADMIN.logoutButton}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-rose-600 transition-colors"
                        >
                            <LogOut className="w-4 h-4" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="container-x py-8 lg:py-10">
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <StatCard testid={ADMIN.statCard("total")} icon={Inbox} label="Total enquiries" value={stats?.total ?? "—"} accent="emerald" />
                    <StatCard testid={ADMIN.statCard("new")} icon={TrendingUp} label="New this week" value={stats?.last_7_days ?? "—"} accent="blue" />
                    <StatCard testid={ADMIN.statCard("in-review")} icon={BarChart3} label="In review" value={stats?.by_status?.in_review ?? 0} accent="amber" />
                    <StatCard testid={ADMIN.statCard("won")} icon={BarChart3} label="Won" value={stats?.by_status?.won ?? 0} accent="emerald" />
                </div>

                {/* Filters */}
                <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[200px]">
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Search</Label>
                        <div className="relative mt-1.5">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                data-testid={ADMIN.searchInput}
                                value={filter.q}
                                onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))}
                                placeholder="Name, email, postcode, reference…"
                                className="pl-9 h-11 rounded-xl"
                            />
                        </div>
                    </div>
                    <div className="w-full sm:w-52">
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sector</Label>
                        <Select value={filter.sector} onValueChange={(v) => setFilter((f) => ({ ...f, sector: v }))}>
                            <SelectTrigger data-testid={ADMIN.sectorFilter} className="mt-1.5 h-11 rounded-xl"><SelectValue placeholder="All sectors" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All sectors</SelectItem>
                                {SECTORS.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="w-full sm:w-44">
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</Label>
                        <Select value={filter.status} onValueChange={(v) => setFilter((f) => ({ ...f, status: v }))}>
                            <SelectTrigger data-testid={ADMIN.statusFilter} className="mt-1.5 h-11 rounded-xl"><SelectValue placeholder="All statuses" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All statuses</SelectItem>
                                {STATUS_OPTIONS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={load} data-testid={ADMIN.refreshButton} className="h-11 px-4 rounded-xl border border-slate-200 hover:border-emerald-300 flex items-center gap-2 text-sm font-semibold text-slate-700">
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                        </button>
                        <button onClick={exportCsv} data-testid={ADMIN.exportButton} className="h-11 px-4 rounded-xl bg-navy-900 hover:bg-navy-800 text-white flex items-center gap-2 text-sm font-semibold">
                            <Download className="w-4 h-4" /> Export CSV
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                        <div>
                            <h2 className="text-base font-display font-semibold text-navy-900">Enquiries</h2>
                            <div className="text-xs text-slate-500 mt-0.5">{total} total{filter.q || filter.status !== "all" || filter.sector !== "all" ? " (filtered)" : ""}</div>
                        </div>
                    </div>

                    {loading && items.length === 0 ? (
                        <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
                    ) : items.length === 0 ? (
                        <div className="py-20 text-center text-slate-500 text-sm">No enquiries yet. Submit a test claim from the public site to see it here.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid={ADMIN.enquiriesTable}>
                                <TableHeader>
                                    <TableRow className="bg-slate-50">
                                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Reference</TableHead>
                                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Name</TableHead>
                                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Sector</TableHead>
                                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Submitted</TableHead>
                                        <TableHead></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {items.map((e) => (
                                        <TableRow
                                            key={e.id}
                                            data-testid={ADMIN.row(e.id)}
                                            className="cursor-pointer hover:bg-emerald-50/40 transition-colors"
                                            onClick={() => openEnquiry(e)}
                                        >
                                            <TableCell className="font-mono text-xs font-bold text-navy-900">{e.reference}</TableCell>
                                            <TableCell>
                                                <div className="font-semibold text-navy-900 text-sm">{e.full_name}</div>
                                                <div className="text-xs text-slate-500">{e.email}</div>
                                            </TableCell>
                                            <TableCell className="text-sm text-slate-700">{e.sector_name}</TableCell>
                                            <TableCell>{statusBadge(e.status)}</TableCell>
                                            <TableCell className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</TableCell>
                                            <TableCell className="text-right">
                                                <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </main>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent data-testid={ADMIN.detailDialog} className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    {selected && (
                        <>
                            <DialogHeader>
                                <div className="flex items-center gap-3">
                                    <DialogTitle className="font-mono text-xl text-navy-900">{selected.reference}</DialogTitle>
                                    {statusBadge(selected.status)}
                                </div>
                                <DialogDescription className="text-sm text-slate-500">{selected.sector_name} · Submitted {new Date(selected.created_at).toLocaleString("en-GB")}</DialogDescription>
                            </DialogHeader>

                            <div className="grid sm:grid-cols-2 gap-4 text-sm">
                                <Field label="Full name" value={selected.full_name} />
                                <Field label="Email" value={<a className="text-emerald-700" href={`mailto:${selected.email}`}>{selected.email}</a>} />
                                <Field label="Phone" value={<a className="text-emerald-700" href={`tel:${selected.phone}`}>{selected.phone}</a>} />
                                <Field label="Postcode" value={selected.postcode} />
                                <Field label="GDPR consent" value={selected.gdpr_consent ? "Yes" : "No"} />
                                <Field label="Marketing consent" value={selected.marketing_consent ? "Yes" : "No"} />
                            </div>

                            {selected.details && Object.keys(selected.details).length > 0 && (
                                <div className="mt-4">
                                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Claim details</div>
                                    <div className="bg-slate-50 rounded-xl p-4 space-y-2.5">
                                        {Object.entries(selected.details).map(([k, v]) => (
                                            v != null && v !== "" && (
                                                <div key={k} className="grid grid-cols-3 gap-3 text-sm">
                                                    <div className="text-slate-500 font-medium col-span-1 capitalize">{k.replace(/_/g, " ")}</div>
                                                    <div className="text-navy-900 col-span-2">{Array.isArray(v) ? v.join(", ") : String(v)}</div>
                                                </div>
                                            )
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="mt-4 grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Update status</Label>
                                    <Select value={draftStatus} onValueChange={setDraftStatus}>
                                        <SelectTrigger data-testid={ADMIN.statusUpdate} className="mt-1.5 h-11 rounded-xl"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {STATUS_OPTIONS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="mt-3">
                                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Internal notes</Label>
                                <Textarea
                                    data-testid={ADMIN.notesInput}
                                    value={draftNotes}
                                    onChange={(e) => setDraftNotes(e.target.value)}
                                    placeholder="Add a note about this enquiry…"
                                    className="mt-1.5 min-h-[100px] rounded-xl"
                                />
                            </div>

                            <DialogFooter className="mt-4 flex flex-row items-center justify-between gap-3">
                                <button
                                    onClick={deleteEnquiry}
                                    data-testid={ADMIN.deleteButton}
                                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 hover:text-rose-700"
                                >
                                    <Trash2 className="w-4 h-4" /> Delete
                                </button>
                                <button
                                    onClick={saveEnquiry}
                                    disabled={saving}
                                    data-testid={ADMIN.saveButton}
                                    className="btn-primary disabled:opacity-50"
                                >
                                    {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Save changes"}
                                </button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, accent = "emerald", testid }) {
    const tones = {
        emerald: "bg-emerald-50 text-emerald-700",
        blue: "bg-blue-50 text-blue-700",
        amber: "bg-amber-50 text-amber-700",
        rose: "bg-rose-50 text-rose-700",
    };
    return (
        <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[accent]}`}><Icon className="w-5 h-5" /></div>
            </div>
            <div className="mt-4 text-3xl font-display font-bold text-navy-900 tracking-tight">{value}</div>
            <div className="mt-1 text-xs text-slate-500 font-medium uppercase tracking-wider">{label}</div>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mt-0.5 text-navy-900">{value}</div>
        </div>
    );
}
